import { AgencySource } from "./models";

/**
 * Display label per agency source — the twin of `AGENCY_SOURCE_LABELS` in
 * `backend/models/agency_api/queue.py:26`. Keep the two in step.
 *
 * These exact strings are **persisted**: the create flow sends the label as `agencySourceName`, it
 * lands on `request.agency_source_name`, and `isSameAgencySource` (`utils/utilFunctions.tsx:970-974`)
 * then compares it with `===` when deciding whether a new posting may merge into an existing open
 * shift. A casing change here silently breaks open-shift merge matching instead of failing loudly.
 *
 * Deliberately a map and not a generic `titleCase()`: it replaces two separate hand-rolled
 * `charAt(0).toUpperCase() + slice(1)` hacks, and a generic transform that merely happens to agree
 * with a backend enum-label map for today's two values is the same class of accident this is cleaning up.
 */
export const AGENCY_SOURCE_LABELS: Record<AgencySource, string> = {
	[AgencySource.Nursa]: "Nursa",
	[AgencySource.Clipboard]: "Clipboard",
};

/** The persisted display label for an agency source. See `AGENCY_SOURCE_LABELS`. */
export const agencySourceLabel = (source: AgencySource): string => AGENCY_SOURCE_LABELS[source];

/**
 * One department's per-agency activation row, as `/user_info` carries it (PR3).
 *
 * The twin of `AgencyIntegrationState` in `backend/models/entities/agency.py:57`. The persisted shape
 * is an `hris` row with `type='agency'`, so `facilityId` is the vendor id off THAT row — which is the
 * source of truth after PR1, and is not always the same thing as `department.nursa_facility_id` /
 * `clipboard_facility_id` (a department can be mapped through the row while the legacy column is
 * NULL). `facilityId` is never blank and never absent: a row with no facility id is deleted rather
 * than kept, so "unmapped" is the ABSENCE of an entry, never an entry with an empty id.
 *
 * The row carries **no audit fields**. Who moved the switch and when is the Activity Log's job —
 * `hris.updated_by` plus the `staff_change_log` trigger — so nothing here has to be kept in step with
 * it, and the UI never renders a half-answer from a column that may never have been stamped.
 */
export type AgencyIntegrationState = {
	source: AgencySource;
	active: boolean;
	facilityId: string;
};

/**
 * The narrow structural shape these helpers read off a department — the same param pattern as
 * `hasHrisIntegrationOfType`'s `WithHrisIntegrations` in `hrisIntegrations.ts`.
 *
 * `agencyIntegrations` is declared HERE and optional, rather than on the `Department` interface in
 * `contexts/AuthProvider.tsx`. `Department` lacks the property entirely and still satisfies a param
 * whose property is optional, so the seam compiles today and PR3 only has to add the real field —
 * no second refactor.
 */
export type AgencyAvailabilityInput = {
	agencyIntegrations?: AgencyIntegrationState[];
};

/** This department's agency row for one source, or `undefined` when the agency was never mapped. */
export const getAgencyIntegration = (department: AgencyAvailabilityInput, source: AgencySource): AgencyIntegrationState | undefined =>
	department.agencyIntegrations?.find((entry) => entry.source === source);

/**
 * Every agency's facility id, whether or not the integration is active.
 *
 * Reads the `agencyIntegrations` rows and NOTHING else. The legacy
 * `department.nursa_facility_id` / `clipboard_facility_id` fallback is gone: the `hris` agency row
 * is the source of truth, and the two genuinely disagree — SW.Montana is mapped through the row
 * while its column was cleared by hand on 2026-09-17 to stop outbound traffic, and the restore
 * runbook deliberately does not re-populate the column, because that would re-arm the paths still
 * reading it. Column-first, that department's Nursa would be filtered out of `getAvailableAgencies`
 * even with the switch ON — the toggle would look on and post nothing.
 *
 * This is a READ-side cutover only. `_mirror_agency_facility_id` still writes the columns in the
 * same transaction as the row, so they stay in step and a code rollback still finds a correct
 * column; PR5 is what drops them.
 */
export const getAgencyFacilityIdMap = (department: AgencyAvailabilityInput): Record<AgencySource, string | undefined> => ({
	[AgencySource.Nursa]: getAgencyIntegration(department, AgencySource.Nursa)?.facilityId,
	[AgencySource.Clipboard]: getAgencyIntegration(department, AgencySource.Clipboard)?.facilityId,
});

/**
 * Whether the department's integration with `source` is switched on.
 *
 * No default any more, and its removal is part of the same cutover. While the columns were still
 * read, an absent row meant "this client cannot see activation state" — an old token, a cached
 * `/user_info`, any department before the backfill — so it had to answer ACTIVE or every live
 * integration would have blacked out. Now that availability comes from the rows alone, an absent row
 * means the department is not mapped at all: `getAgencyFacilityIdMap` already answers `undefined`
 * for it and `getAvailableAgencies` filters it out before this is consulted. Keeping the old `true`
 * would leave a default answering a question nobody asks any more.
 *
 * The fail-closed decision still lives on the SERVER, where `is_agency_active`
 * (`backend/services/agency_activation.py`) has no default and no fallback. This only governs what
 * the UI *offers*; the server refuses regardless.
 */
const isAgencyIntegrationActive = (department: AgencyAvailabilityInput, source: AgencySource): boolean =>
	getAgencyIntegration(department, source)?.active === true;

/**
 * Agencies this department has a facility id for AND has not deactivated.
 *
 * ITERATION ORDER IS BEHAVIOUR-CRITICAL. `Object.values(AgencySource)` yields `[Nursa, Clipboard]`,
 * and both `CreateShiftContent` and `PostToAgencyDialog` default their selection to element `[0]`, so
 * re-ordering silently flips which agency is pre-selected for a two-agency department.
 */
export const getAvailableAgencies = (department: AgencyAvailabilityInput): AgencySource[] => {
	const facilityIdMap = getAgencyFacilityIdMap(department);
	return Object.values(AgencySource).filter((source) => facilityIdMap[source] && isAgencyIntegrationActive(department, source));
};

/**
 * Agencies this department IS mapped to but has switched off — the gap between "configured" and
 * "offered". The complement of `getAvailableAgencies` over the mapped set, and the one number that
 * answers "how often does an admin not get offered an agency the department actually has?".
 *
 * Kept next to the availability rule rather than derived at a call site so the two can never disagree
 * about what "mapped" means.
 */
export const getSuppressedAgencies = (department: AgencyAvailabilityInput): AgencySource[] => {
	const facilityIdMap = getAgencyFacilityIdMap(department);
	return Object.values(AgencySource).filter((source) => facilityIdMap[source] && !isAgencyIntegrationActive(department, source));
};
